import { gql } from '@apollo/client'
import { useFeatureFlags } from '@npco/mp-utils-mp-feature-flags'
import { ErrorLogger } from '@npco/utils-error-logger'
import { useTranslations } from '@npco/utils-translations'
import {
  Box,
  BREAKPOINT,
  COLOR,
  Flex,
  NewBadge,
  SvgIcon,
  useIsMobileResolution,
} from '@npco/zeller-design-system'

import { ReactComponent as SavingsAccountBannerIcon } from 'assets/svg/accounts/savings-account-banner.svg'
import { SavingsAccountAvatar } from 'components/SavingsAccountAvatar'
import { translations } from 'translations/shared.i18n'

import { ActivateButton } from './ActivateButton/ActivateButton'
import { Checklist } from './CheckList/Checklist'
import { Emphasis } from './Emphasis/Emphasis'
import { LearnMoreButton } from './LearnMoreButton/LearnMoreButton'
import { SavingsAccountBannerEntityFragment } from './SavingsAccountBanner.generated'
import { savingsAccountBannerTranslations } from './SavingsAccountBanner.i18n'
import * as styled from './SavingsAccountBanner.styled'
import { TermsAndConditions } from './TermsAndConditions/TermsAndConditions'

type SavingsAccountBannerProps = {
  entity: SavingsAccountBannerEntityFragment
}

export const SavingsAccountBanner = ({ entity }: SavingsAccountBannerProps) => {
  const isMobileResolution = useIsMobileResolution(BREAKPOINT.XS + 1)
  const isTabletResolution = useIsMobileResolution(BREAKPOINT.SM + 1)

  const flags = useFeatureFlags()

  const backgroundColor = flags.SavingsAccountMay
    ? COLOR.DARK_BLUE
    : COLOR.BLUE_80

  const defaultTextColour = flags.SavingsAccountMay ? COLOR.WHITE : COLOR.BLACK

  const t = useTranslations(savingsAccountBannerTranslations)
  const tShared = useTranslations(translations)

  if (!entity.savingsAccountProduct?.effectiveInterestRate) {
    ErrorLogger.reportError(
      '[Banking] - Savings Account Product interest rate NULL on Entity',
      undefined
    )
    return null
  }

  return (
    <Box
      borderRadius="8px"
      backgroundColor={backgroundColor}
      color={defaultTextColour}
      p="32px 24px 24px 24px"
      position="relative"
      mx={{ _: '-12px', SM: '-24px' }}
      mb="40px"
      mt={{ _: '12px', XS: 'unset' }}
    >
      {isMobileResolution && (
        <Box position="absolute" top="-12px" left="16px">
          <NewBadge text={tShared('new')} />
        </Box>
      )}
      {!isTabletResolution && (
        <Box position="absolute" right="0" top="0">
          <SvgIcon>
            <SavingsAccountBannerIcon />
          </SvgIcon>
        </Box>
      )}
      <Box pb="24px" mr={{ _: 'unset', SM: '160px' }}>
        <styled.BannerTitle>
          {t('title', {
            interest: (
              <Emphasis
                entitySavingsAccountProduct={entity.savingsAccountProduct}
              />
            ),
          })}
        </styled.BannerTitle>
      </Box>
      <Checklist />
      {!isMobileResolution && (
        <Flex
          backgroundColor={COLOR.WHITE}
          color={COLOR.BLACK}
          alignItems="center"
          height="64px"
          p="0 6px 0 6px"
          mb="16px"
          justifyContent="space-between"
          borderRadius="8px"
        >
          <Flex alignItems="center">
            <Box pr="12px">
              <SavingsAccountAvatar />
            </Box>
            <Box pr="8px">{t('savingsAccount')}</Box>
            {!isTabletResolution && (
              <Box>
                <NewBadge text="New" />
              </Box>
            )}
          </Flex>
          {isTabletResolution ? (
            <Box pr="6px">
              <NewBadge text="New" />
            </Box>
          ) : (
            <Flex alignItems="center" gap="8px">
              <Box>
                <LearnMoreButton entity={entity} />
              </Box>
              <Box>
                <ActivateButton entity={entity} />
              </Box>
            </Flex>
          )}
        </Flex>
      )}
      <TermsAndConditions
        savingsAccountProduct={entity.savingsAccountProduct}
      />
      {isTabletResolution && (
        <Flex alignItems="center" gap="8px" pt="24px">
          <Box width="50%">
            <LearnMoreButton entity={entity} fullWidth />
          </Box>
          <Box width="50%">
            <ActivateButton fullWidth entity={entity} />
          </Box>
        </Flex>
      )}
    </Box>
  )
}

SavingsAccountBanner.fragments = {
  Entity: gql`
    fragment SavingsAccountBannerEntityFragment on Entity {
      savingsAccountProduct {
        ...EmphasisEntitySavingsAccountProductFragment
        ...TermsAndConditionsEntitySavingsAccountProductFragment
      }
      ...ActivateButtonEntityFragment
      ...LearnMoreButtonEntityFragment
    }

    ${TermsAndConditions.fragments.EntitySavingsAccountProduct}
    ${ActivateButton.fragments.Entity}
    ${LearnMoreButton.fragments.Entity}
    ${Emphasis.fragments.EntitySavingsAccountProduct}
  `,
}
